import BasicButton from "@/components/button/BasicButton"
import Toggle from "@/components/form/toggle/Toggle"
import Icon from "@/components/icon/Icon"
import { Menu, MenuItem } from "@mui/material"

import "./activityDetailHeader.scss"
import { colors } from "@/components/colors"
import {
  createActivityCommonStore,
  createActivityStore,
  createSessionStore,
} from "@/app/activity/store"
import { Magnification } from "@/app/activity/components/timeline/activityTimeline/type"
import IconButton from "@/components/button/IconButton"
import { useTranslation } from "react-i18next"
import { useMemo, useRef, useState } from "react"
import {
  useAnalyzingActivityQuery,
  useCreateActivityAnalyzeMutation,
} from "@/hooks/api/useActivityQuery"
import { createGlobalStore } from "@/stores/global"
import useRouter from "@/hooks/router/useRoute"
import { ActivityDetailHeaderProps } from "@/app/activity/detail/activityDetailHeader/type.ts"
import SortSelector from "@/app/report/components/setting/SortSelector"
import PlayerSelector from "@/app/report/components/filter/playerSelector/PlayerSelector"
import Divider from "@/components/divider/Divider"
import { ReportStatus } from "@/types/report"

export default function ActivityDetailHeader({
  sessionLength,
  playerLength,
  isCropping,
  setIsCropping,
  isCropSaveDisabled,
  isCropDirty,
  isApplyToAllSessionsActive,
  onClickSaveCrop,
  onClickApplyToAllSessions,
  resetCropData,
  activityId,
  allDataPlayers,
  selectedPlayerList,
  setSelectedPlayerList,
}: ActivityDetailHeaderProps) {
  const { t } = useTranslation()
  const router = useRouter()

  const { activity } = createActivityStore((state) => ({
    activity: state.activity,
  }))

  const { session } = createSessionStore((state) => ({
    session: state.session,
  }))

  const { magnification, setMagnification, sortKey, setSortKey, sortDirection, setSortDirection } =
    createActivityCommonStore((state) => ({
      magnification: state.magnification,
      setMagnification: state.setMagnification,
      sortKey: state.sortKey,
      setSortKey: state.setSortKey,
      sortDirection: state.sortDirection,
      setSortDirection: state.setSortDirection,
    }))

  const { setSnackBar } = createGlobalStore((state) => ({
    setSnackBar: state.setSnackBar,
  }))

  const [playerSelectorOpen, setPlayerSelectorOpen] = useState(false)

  const sortOptions = useMemo(
    () => [
      {
        key: "uniformNumber",
        label: t("uniformNumber"),
        children: [
          {
            key: "uniformNumber-asc",
            label: t("lowToHigh", { defaultValue: "Low to High" }),
            value: { key: "uniformNumber", order: "asc" as const },
          },
          {
            key: "uniformNumber-desc",
            label: t("highToLow", { defaultValue: "High to Low" }),
            value: { key: "uniformNumber", order: "desc" as const },
          },
        ],
      },
      {
        key: "nickname",
        label: t("playerName"),
        children: [
          {
            key: "nickname-asc",
            label: t("aToZ", { defaultValue: "A - Z" }),
            value: { key: "nickname", order: "asc" as const },
          },
          {
            key: "nickname-desc",
            label: t("zToA", { defaultValue: "Z - A" }),
            value: { key: "nickname", order: "desc" as const },
          },
        ],
      },
      {
        key: "position",
        label: t("position"),
        children: [
          {
            key: "position-asc",
            label: t("fwToGk", { defaultValue: "FW - GK" }),
            value: { key: "position", order: "asc" as const },
          },
          {
            key: "position-desc",
            label: t("gkToFw", { defaultValue: "GK - FW" }),
            value: { key: "position", order: "desc" as const },
          },
        ],
      },
    ],
    [t],
  )

  const { data: analysisStatus } = useAnalyzingActivityQuery(activity?.idfActivity)
  const { mutateAsync: runAnalysis, isPending: isRunningAnalysis } =
    useCreateActivityAnalyzeMutation()

  const isAnalysisBtnDisabled = useMemo(() => {
    return (
      analysisStatus?.status === ReportStatus.Analyzed || sessionLength === 0 || playerLength === 0
    )
  }, [analysisStatus?.status, sessionLength, playerLength])

  const zoomIn = () => {
    if (magnification !== Magnification.QUAD) {
      setMagnification((magnification || Magnification.INIT) * 2)
    }
  }

  const zoomOut = () => {
    if (magnification !== Magnification.HALF) {
      setMagnification((magnification || Magnification.INIT) / 2)
    }
  }

  const resetZoom = () => {
    setMagnification(Magnification.INIT)
  }

  const handleRunAnalysis = async () => {
    try {
      await runAnalysis(activity?.idfActivity as number)

      setSnackBar({
        message: t("messages.successUpdate"),
        severity: "success",
        open: true,
      })

      router?.back()
    } catch (error) {
      console.error("Error running analysis:", error)
    }
  }

  return (
    <div className="ActivityDetailHeaderCont">
      <div className="back-btn-cont">
        <BasicButton
          className="back-btn"
          type="ghost"
          size="md"
          onClick={(e) => {
            e.stopPropagation()

            if (isCropping) {
              if (isCropDirty) {
                if (!window.confirm(t("messages.confirmLeavePage"))) {
                  return
                }
                resetCropData?.()
              }
              setIsCropping(false)

              return
            }

            router?.push("/activity/list")
          }}>
          <Icon type="arrow-line-left" />
          Back
        </BasicButton>
      </div>
      <div className="crop-btn-magnify-cont">
        <Toggle
          disabled={!session}
          className="toggle-crop-mode-switch"
          icon={<Icon type="crop-line" color={colors.neutral.grey[600]} />}
          checkedIcon={<Icon type="crop-line" color={colors.neutral.grey[600]} />}
          checked={isCropping}
          onChange={(e) => {
            const isEnteringCropMode = e.target.checked

            // 크롭 모드를 끌 때 변경사항이 있으면 경고 표시
            if (!isEnteringCropMode && isCropDirty) {
              if (!window.confirm(t("messages.confirmLeavePage"))) {
                return
              }
              // 확인하면 데이터 초기화
              resetCropData?.()
            }

            setIsCropping(isEnteringCropMode)
          }}
        />
        <div className="filter-and-magnify-cont">
          <div className="player-filter-selectors">
            <PlayerSelector
              open={playerSelectorOpen}
              setOpen={setPlayerSelectorOpen}
              selectedActivity={activityId ? [activityId] : []}
              players={allDataPlayers.length > 0 ? allDataPlayers : undefined}
              selectedPlayerList={selectedPlayerList}
              setSelectedPlayerList={setSelectedPlayerList}
              paramsVersion={false}
              liveUpdate
            />
            <SortSelector
              value={{ key: sortKey, order: sortDirection }}
              onChange={(val) => {
                setSortKey(val.key as "uniformNumber" | "nickname" | "position")
                setSortDirection(val.order)
              }}
              options={sortOptions}
              menuClassName="activity-sort-selector-menu"
              renderTrigger={(handleOpen) => (
                <IconButton type="ghost" size="md" icon="filter" onClick={handleOpen} />
              )}
            />
          </div>
          <Divider orientation="vertical" />
          <div className="magnify-btn-cont">
            <p className="magnification-percentage">
              {(magnification || Magnification.INIT) * 100}%
            </p>
            <IconButton
              disabled={(magnification || Magnification.INIT) === Magnification.QUAD}
              type="ghost"
              size="md"
              icon="zoom-in"
              onClick={zoomIn}></IconButton>
            <IconButton
              disabled={(magnification || Magnification.INIT) === Magnification.HALF}
              type="ghost"
              size="md"
              icon="zoom-out"
              onClick={zoomOut}></IconButton>
            <IconButton
              disabled={magnification === Magnification.INIT}
              type="ghost"
              size="md"
              icon="reset"
              onClick={resetZoom}></IconButton>
          </div>
        </div>
      </div>
      <div className="run-analysis-btn-cont">
        {isCropping && onClickApplyToAllSessions ? (
          <ApplySplitButton
            disabled={isCropSaveDisabled}
            isAllSessions={!!isApplyToAllSessionsActive}
            onApplyCurrent={() => onClickSaveCrop?.(false)}
            onApplyAll={() => onClickSaveCrop?.(true)}
          />
        ) : (
          <BasicButton
            disabled={isCropping ? isCropSaveDisabled : isAnalysisBtnDisabled}
            isLoading={!isCropping && isRunningAnalysis}
            className="run-analysis-btn"
            size="md"
            type="primary-contained"
            onClick={isCropping ? () => onClickSaveCrop?.() : handleRunAnalysis}>
            {t(isCropping ? "button.done" : "button.runAnalysis")}
          </BasicButton>
        )}
      </div>
    </div>
  )
}

function ApplySplitButton({
  disabled,
  isAllSessions,
  onApplyCurrent,
  onApplyAll,
}: {
  disabled?: boolean
  isAllSessions: boolean
  onApplyCurrent: () => void
  onApplyAll: () => void
}) {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  // 메인 클릭 = 현재 모드 (toggle 상태) 그대로 적용 — split-button-with-default 패턴
  const handleMainClick = () => {
    if (isAllSessions) {
      onApplyAll()
    } else {
      onApplyCurrent()
    }
  }

  return (
    <>
      <div ref={anchorRef} className="apply-split-button">
        <BasicButton
          disabled={disabled}
          className="apply-split-button__main"
          size="md"
          type="primary-contained"
          onClick={handleMainClick}>
          {t("button.apply", { defaultValue: "Apply" })}
        </BasicButton>
        <button
          type="button"
          disabled={disabled}
          className="apply-split-button__chevron"
          aria-label="Apply options"
          onClick={() => setMenuOpen(true)}>
          <Icon
            type="arrow-down"
            color={disabled ? colors.neutral.grey[500] : colors.neutral.white}
          />
        </button>
      </div>
      <Menu
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: "apply-split-button__menu" } }}>
        <MenuItem
          onClick={() => {
            setMenuOpen(false)
            onApplyCurrent()
          }}>
          {t("button.currentSessionOnly", { defaultValue: "Current session only" })}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuOpen(false)
            onApplyAll()
          }}>
          {t("allSessions")}
        </MenuItem>
      </Menu>
    </>
  )
}
